import { Dropdown } from '@eventbrite/eds-containers';
import {
    clearNavigatorRecentSearchTerms,
    getNavigatorLastRecentSearchTerms,
} from '@eventbrite/personalization';
import React, { useState } from 'react';
import {
    CompletionType,
    DEFAULT_COMPLETION_TYPES,
} from '../../../api/searchAutocomplete';
import SearchQuerySuggestions from '../../../containers/SearchSuggestions';
import { useSearchInputContext } from '../../../context';
import { useSearchAutocompleteQuery } from '../../../hooks';
import { SearchAutocompleteResponse } from '../../../types';
import { AISearchCTA } from './AISearchCTA';
import RecentSearches from './RecentSearches/RecentSearches';
import './SearchDropdown.scss';
import TrendingSearches from './TrendingSearches/TrendingSearches';

export type Props = {
    onClickOutside: (e?: React.MouseEvent<HTMLElement>) => void;
    isAISearchEnabled?: boolean;
    openAISearch: () => void;
};

const getDropdownContent = ({
    completionTypes,
    autocompleteData,
    handleClearRecentSearches,
    recentSearches,
    query,
}: {
    completionTypes: CompletionType[];
    autocompleteData?: SearchAutocompleteResponse;
    handleClearRecentSearches: () => void;
    recentSearches: string[];
    query?: string;
}) => {
    const hasAutocompleteData =
        !!autocompleteData?.query.length ||
        !!autocompleteData?.organizer?.length;

    if (query && hasAutocompleteData) {
        return <SearchQuerySuggestions completionTypes={completionTypes} />;
    } else if (query && !hasAutocompleteData) {
        return null;
    } else if (recentSearches.length > 0) {
        return (
            <RecentSearches
                recentSearches={recentSearches}
                handleClearRecentSearches={handleClearRecentSearches}
            />
        );
    }

    return <TrendingSearches />;
};

const SearchDropdown: React.FunctionComponent<Props> = ({
    onClickOutside,
    isAISearchEnabled,
    openAISearch,
}) => {
    const { completionTypes = DEFAULT_COMPLETION_TYPES } =
        useSearchInputContext();

    const [recentSearches, setRecentSearches] = useState(
        getNavigatorLastRecentSearchTerms(5),
    );

    const handleClearRecentSearches = React.useCallback(() => {
        clearNavigatorRecentSearchTerms();
        setRecentSearches([]);
    }, [setRecentSearches]);

    const { query } = useSearchInputContext();
    const { data: autocompleteData } = useSearchAutocompleteQuery({
        completionTypes,
    });

    const content = React.useMemo(
        () =>
            getDropdownContent({
                completionTypes,
                autocompleteData: autocompleteData || undefined,
                handleClearRecentSearches,
                recentSearches,
                query,
            }),
        [
            autocompleteData,
            completionTypes,
            handleClearRecentSearches,
            query,
            recentSearches,
        ],
    );

    if (!content) {
        return null;
    }

    return (
        <Dropdown onClickOutside={onClickOutside}>
            <div
                className="search-takeover-wrapper"
                data-testid="searchbar-dropdown"
            >
                <AISearchCTA
                    isEnabled={Boolean(isAISearchEnabled)}
                    onClick={openAISearch}
                />
                <div className="search-takeover-content">{content}</div>
            </div>
        </Dropdown>
    );
};

export default SearchDropdown;
